import React, { useEffect, useState } from "react";
import { ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OtcRequestItem } from "../../types";
import { Button, Input } from "../../../../shared/ui";
import { useOtcBanks, useVerifyBankDetail } from "../../hooks";
import { formatCurrencyAmount } from "../../../transactions/utils/formatCurrency";
import { FormField } from "./FormField";
import { FormSelect } from "./FormSelect";
import { RecipientAddressFields } from "./RecipientAddressFields";
import { useProviderLocations } from "./useProviderLocations";
import { CurrencyFlag } from "./CurrencyFlag";
import {
  LOCATION_OPTIONS,
  PAYMENT_METHOD_OPTIONS_OUTSIDE_US,
  PAYMENT_METHOD_OPTIONS_US,
  ACCOUNT_HOLDER_TYPE_OPTIONS,
  RELATIONSHIP_OPTIONS,
} from "./AddNewProviderForm.constants";
import { validateNewProviderForm } from "./validateNewProviderForm";

export interface NewProviderData {
  recipientLocation: string;
  recipientCountry: string;
  paymentMethod?: string;
  bank: string;
  bankCode?: string;
  bankCountry?: string;
  accountHolderType: string;
  firstName: string;
  lastName: string;
  email?: string;
  relationship: string;
  accountNumber: string;
  swiftCode?: string;
  routingNumber?: string;
  addressCountry: string;
  streetAddress: string;
  streetAddress2?: string;
  city: string;
  state: string;
  zipCode: string;
  accountName?: string;
  nameEnquiryReference?: string;
}

export interface AddNewProviderFormProps {
  request: OtcRequestItem;
  onBack: () => void;
  onConfirm: (data: NewProviderData) => void;
}

export const AddNewProviderForm: React.FC<AddNewProviderFormProps> = ({
  request,
  onBack,
  onConfirm,
}) => {
  // Source currency must come strictly from the passed OTC request state without fallback
  const sourceCurrency = request.currency.toUpperCase();
  const targetCurrency = request.currencyTo ? request.currencyTo.toUpperCase() : "";

  // If currency is not USD (e.g. NGN), only show bank dropdown + account number + validated name
  const isUsd = sourceCurrency === "USD";

  const formattedAmount = formatCurrencyAmount(request.amount, sourceCurrency);

  // Extract customerSource if present on transaction response and not null
  const rawCustomerSource =
    request.customerSource ??
    (request as any).transaction?.customerSource ??
    (request as any).transaction?.otc?.customerSource;
  const customerSource =
    rawCustomerSource &&
    typeof rawCustomerSource === "string" &&
    rawCustomerSource.trim() &&
    rawCustomerSource.trim().toLowerCase() !== "null" &&
    rawCustomerSource.trim().toLowerCase() !== "undefined"
      ? rawCustomerSource.trim()
      : undefined;

  // Fetch banks using the OTC currencies dynamically with optional customer source
  const { banks, isLoading: isLoadingBanks } = useOtcBanks({
    currencyFrom: sourceCurrency,
    currencyTo: sourceCurrency,
    source: customerSource,
  });

  const {
    verify,
    isVerifying,
    verifiedData,
    error: verificationError,
    reset: resetVerification,
  } = useVerifyBankDetail({ source: customerSource });

  const [formData, setFormData] = useState<NewProviderData>({
    recipientLocation: isUsd ? "NOT_US" : "NOT_US",
    recipientCountry: isUsd ? "" : "",
    paymentMethod: isUsd ? "IBAN / SWIFT" : "",
    bank: "",
    bankCode: "",
    bankCountry: "",
    accountHolderType: "Individual",
    firstName: "",
    lastName: "",
    email: request.customer?.email || "",
    relationship: "",
    accountNumber: "",
    swiftCode: "",
    routingNumber: "",
    addressCountry: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    accountName: "",
    nameEnquiryReference: "",
  });

  const isOutsideUs = isUsd ? formData.recipientLocation === "NOT_US" : false;

  const { allCountryOptions, stateOptions, cityOptions } = useProviderLocations({
    isDomestic: !isUsd || isOutsideUs,
    recipientCountry: formData.addressCountry || formData.recipientCountry,
    state: formData.state,
  });

  const handleChange = (field: keyof NewProviderData, value: string) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "recipientCountry" || field === "addressCountry") {
        next.state = "";
        next.city = "";
      } else if (field === "state") {
        next.city = "";
      } else if (field === "recipientLocation") {
        if (value === "US") {
          next.recipientCountry = "US";
          next.addressCountry = "US";
          next.bankCountry = "US";
          next.paymentMethod = "ACH / Fedwire";
          next.swiftCode = "";
          next.state = "";
          next.city = "";
        } else {
          next.recipientCountry = "";
          next.addressCountry = "";
          next.bankCountry = "";
          next.paymentMethod = "IBAN / SWIFT";
          next.routingNumber = "";
          next.state = "";
          next.city = "";
        }
      }
      return next;
    });
  };

  const handleBankSelect = (selectedCode: string) => {
    const selectedBank = banks.find((b) => b.code === selectedCode);
    setFormData((prev) => ({
      ...prev,
      bankCode: selectedCode,
      bank: selectedBank ? selectedBank.name : selectedCode,
    }));
    resetVerification();
  };

  // Auto-verify when 10-digit account number and bank code are supplied (non-USD)
  useEffect(() => {
    const cleanAccount = formData.accountNumber.trim();
    if (!isUsd && cleanAccount.length === 10 && formData.bankCode) {
      verify({
        accountNumber: cleanAccount,
        bankCode: formData.bankCode,
        currency: sourceCurrency,
        source: customerSource,
      });
    }
  }, [formData.accountNumber, formData.bankCode, isUsd, sourceCurrency, verify, customerSource]);

  // Sync verified account name into form data
  useEffect(() => {
    if (verifiedData?.accountName) {
      const parts = verifiedData.accountName.split(" ");
      setFormData((prev) => ({
        ...prev,
        firstName: parts[0] || prev.firstName,
        lastName: parts.slice(1).join(" ") || prev.lastName,
        accountName: verifiedData.accountName,
        nameEnquiryReference: verifiedData.nameEnquiryReference,
      }));
    }
  }, [verifiedData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isUsd) {
      // Non-USD minimal validation: Bank + Account number + Account holder name
      if (!formData.bankCode) {
        toast.error("Please select a bank");
        return;
      }
      if (!formData.accountNumber.trim()) {
        toast.error("Please enter an account number");
        return;
      }
      const resolvedName =
        verifiedData?.accountName ||
        formData.accountName ||
        `${formData.firstName} ${formData.lastName}`.trim();
      if (!resolvedName) {
        toast.error("Please enter or verify the account holder name");
        return;
      }
    } else {
      // Full USD validation
      const error = validateNewProviderForm(formData);
      if (error) {
        toast.error(error);
        return;
      }
    }

    onConfirm({
      ...formData,
      accountName:
        verifiedData?.accountName ||
        formData.accountName ||
        `${formData.firstName} ${formData.lastName}`.trim(),
      nameEnquiryReference:
        verifiedData?.nameEnquiryReference || formData.nameEnquiryReference,
    });
  };

  const bankOptions = banks.map((b) => ({ value: b.code, label: b.name }));

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <h2 className="text-[20px] font-semibold text-neutral-900">
        Add recipient
      </h2>

      {/* Amount banner with dynamic flags from currency and currencyTo */}
      <div className="flex items-center gap-2.5 rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-xs">
        <div className="flex items-center -space-x-1">
          <CurrencyFlag currency={sourceCurrency} className="size-5" />
          <CurrencyFlag currency={targetCurrency} className="size-5" />
        </div>
        <span className="text-[14px] font-semibold text-neutral-900">
          Sending {formattedAmount}
        </span>
      </div>

      {/* Form Fields */}
      <div className="flex flex-col gap-4">
        {!isUsd ? (
          <>
            {/* Non-USD fields: Bank Selection */}
            <FormSelect
              label="Bank"
              value={formData.bankCode || formData.bank}
              onValueChange={handleBankSelect}
              options={bankOptions}
              placeholder="Select bank"
              disabled={isLoadingBanks}
            />

            {/* Account number with verification indicator */}
            <FormField
              label="Account number"
              headerRight={
                <>
                  {isVerifying && (
                    <span className="flex items-center gap-1 text-[12px] font-medium text-brand">
                      <Loader2 size={13} className="animate-spin" /> Verifying account...
                    </span>
                  )}
                </>
              }
              errorMessage={verificationError || undefined}
            >
              <Input
                type="text"
                placeholder="Enter 10-digit account number"
                value={formData.accountNumber}
                onChange={(e) => handleChange("accountNumber", e.target.value)}
                className="h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all"
              />
            </FormField>

            {/* Account holder name */}
            <FormField label="Account name">
              <Input
                type="text"
                placeholder="Account holder name"
                value={
                  formData.accountName ||
                  (formData.firstName ? `${formData.firstName} ${formData.lastName}`.trim() : "")
                }
                onChange={(e) => handleChange("accountName", e.target.value)}
                readOnly={Boolean(verifiedData?.accountName)}
                className={`h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all ${
                  verifiedData?.accountName ? "cursor-default text-neutral-800" : ""
                }`}
              />
            </FormField>
          </>
        ) : (
          /* USD fields matching design screenshot */
          <>
            <FormSelect
              label="Recipient location"
              value={formData.recipientLocation}
              onValueChange={(val) => handleChange("recipientLocation", val)}
              options={LOCATION_OPTIONS}
              placeholder="Select location"
            />

            <FormSelect
              label="Payment method"
              value={formData.paymentMethod || (isOutsideUs ? "IBAN / SWIFT" : "ACH / Fedwire")}
              onValueChange={(val) => handleChange("paymentMethod", val)}
              options={isOutsideUs ? PAYMENT_METHOD_OPTIONS_OUTSIDE_US : PAYMENT_METHOD_OPTIONS_US}
              placeholder="Select payment method"
            />

            <FormField label={isOutsideUs ? "Account number or IBAN" : "Account number"}>
              <Input
                type="text"
                placeholder={isOutsideUs ? "Enter an account number or IBAN" : "Enter recipient's account number"}
                value={formData.accountNumber}
                onChange={(e) => handleChange("accountNumber", e.target.value)}
                className="h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all"
              />
            </FormField>

            {isOutsideUs ? (
              <FormField label="SWIFT or BIC">
                <Input
                  type="text"
                  placeholder="Enter a SWIFT or BIC"
                  value={formData.swiftCode || ""}
                  onChange={(e) => handleChange("swiftCode", e.target.value)}
                  className="h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all"
                />
              </FormField>
            ) : (
              <FormField label="Routing number (ABA)">
                <Input
                  type="text"
                  placeholder="Enter 9-digit routing number"
                  value={formData.routingNumber || ""}
                  onChange={(e) => handleChange("routingNumber", e.target.value)}
                  className="h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all"
                />
              </FormField>
            )}

            <FormField label="Account name">
              <Input
                type="text"
                placeholder="Enter name on account"
                value={formData.accountName || ""}
                onChange={(e) => handleChange("accountName", e.target.value)}
                className="h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all"
              />
              <p className="mt-1.5 text-[12px] text-neutral-500">
                This recipient will be automatically saved as a beneficiary for future transfers
              </p>
            </FormField>

            <FormSelect
              label="Recipient bank country"
              value={formData.bankCountry || ""}
              onValueChange={(val) => handleChange("bankCountry", val)}
              options={allCountryOptions}
              placeholder="Select bank country"
            />

            <FormField label="Recipient bank name">
              <Input
                type="text"
                placeholder="Enter bank name"
                value={formData.bank}
                onChange={(e) => handleChange("bank", e.target.value)}
                className="h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all"
              />
            </FormField>

            <FormSelect
              label="Recipient type"
              value={formData.accountHolderType}
              onValueChange={(val) => handleChange("accountHolderType", val)}
              options={ACCOUNT_HOLDER_TYPE_OPTIONS}
              placeholder="Select recipient type"
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="First name of recipient">
                <Input
                  type="text"
                  placeholder="Enter first name"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  className="h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all"
                />
              </FormField>

              <FormField label="Last name of recipient">
                <Input
                  type="text"
                  placeholder="Enter last name"
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  className="h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all"
                />
              </FormField>
            </div>

            <FormSelect
              label="Relationship"
              value={formData.relationship}
              onValueChange={(val) => handleChange("relationship", val)}
              options={RELATIONSHIP_OPTIONS}
              placeholder="Select relationship with recipient"
            />

            <FormField label="Recipient's email">
              <Input
                type="email"
                placeholder="Enter recipient's email address"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="h-[48px] rounded-[8px] bg-surface-input px-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus-within:border-brand focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-ring transition-all"
              />
            </FormField>

            <RecipientAddressFields
              streetAddress={formData.streetAddress}
              country={formData.addressCountry}
              city={formData.city}
              state={formData.state}
              zipCode={formData.zipCode}
              countryOptions={allCountryOptions}
              cityOptions={cityOptions}
              stateOptions={stateOptions}
              onChange={(field, val) => {
                if (field === "country") {
                  handleChange("addressCountry", val);
                } else {
                  handleChange(field, val);
                }
              }}
            />
          </>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-neutral-100 pt-4">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-[14px] font-medium text-neutral-700 transition-colors hover:text-neutral-900"
        >
          <ChevronLeft size={16} />
          <span>Select from beneficiaries</span>
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="h-10 rounded-[8px] px-4 py-2 text-[14px] font-medium text-neutral-700 transition-colors hover:text-neutral-900"
          >
            Back
          </button>
          <Button type="submit" className="px-6">
            Confirm
          </Button>
        </div>
      </div>
    </form>
  );
};
